/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

import { useCountdown } from '../hooks/useCountdown';
import type { AllocationStatus, LaunchBoardUnit } from '../types';
import { formatCountdown } from './LaunchBoardGrid';
import {
  ALLOCATION_STATUS_DOT,
  ALLOCATION_STATUS_TILE,
  countdownColor,
} from './launch-unit-status';

const STATUS_ORDER: AllocationStatus[] = [
  'available',
  'held',
  'reserved_with_deposit',
  'sold',
];

interface LaunchBoardDesktopMirrorProps {
  units: LaunchBoardUnit[];
  selectedUnitId: string | null;
  onSelectUnit: (unit: LaunchBoardUnit) => void;
}

/** Units of one tower, grouped by floor, highest floor first. */
function floorsOf(units: LaunchBoardUnit[]): Array<[string, LaunchBoardUnit[]]> {
  const floors = new Map<string, LaunchBoardUnit[]>();
  units.forEach((unit) => {
    const key = unit.floor ?? '';
    floors.set(key, [...(floors.get(key) ?? []), unit]);
  });
  return [...floors.entries()].sort((a, b) => Number(b[0]) - Number(a[0]));
}

/** Towers in the order they appear in the board payload. */
function towersOf(units: LaunchBoardUnit[]): Array<[string, LaunchBoardUnit[]]> {
  const towers = new Map<string, LaunchBoardUnit[]>();
  units.forEach((unit) => {
    const key = unit.tower ?? '';
    towers.set(key, [...(towers.get(key) ?? []), unit]);
  });
  return [...towers.entries()];
}

/**
 * One unit as a cell of the mirror.
 *
 * Colour carries the status, so a manager reads the whole tower's state
 * without reading a word. Held cells show their countdown in place of the
 * typology — it is the only thing about them that changes.
 */
function UnitCell({ unit, selected, dimmed, onSelect }: {
  unit: LaunchBoardUnit;
  selected: boolean;
  dimmed: boolean;
  onSelect: (unit: LaunchBoardUnit) => void;
}) {
  const held = unit.allocation_status === 'held';
  const ticking = useCountdown(held ? unit.remaining_seconds : null);

  return (
    <button
      type="button"
      onClick={() => onSelect(unit)}
      className={cn(
        'flex h-[var(--space-14)] flex-col items-center justify-center gap-[var(--space-0-5)]',
        'rounded-(--radius-md) border px-[var(--space-1)]',
        // Only the matching cells keep their status colour. The dim is an
        // inline style rather than an opacity utility because this project
        // ships no .opacity-* rule at all (checked against the built
        // stylesheet: the utility generates nothing), so `opacity-30` would
        // have been dead markup.
        !dimmed && ALLOCATION_STATUS_TILE[unit.allocation_status],
        selected && 'outline-2 outline-offset-1 outline-[var(--seed-base)]',
      )}
      style={{
        transition: 'var(--transition-standard)',
        ...(dimmed
          ? {
              background: 'var(--surface-secondary)',
              borderColor: 'var(--border-subtle)',
              color: 'var(--text-tertiary)',
            }
          : {}),
      }}
    >
      <Text as="span" className="text-[length:var(--text-meta)] [font-weight:var(--font-semibold)]">
        {unit.label}
      </Text>
      {held ? (
        <Text
          as="span"
          className="text-[length:var(--text-meta)] tabular-nums"
          style={{ color: countdownColor(ticking) }}
        >
          {formatCountdown(ticking)}
        </Text>
      ) : (
        <Text as="span" className="w-full truncate text-center text-[length:var(--text-micro)] opacity-80">
          {unit.typology ?? ''}
        </Text>
      )}
    </button>
  );
}

/**
 * The launch board as a sales mirror, for the desktop.
 *
 * A flat grid of cards said nothing about the building. Here one panel is
 * one tower, a row is a floor and a cell is a unit, which is how a launch
 * is actually discussed ("the whole sixth floor is gone"). Filtering by
 * status dims the cells that do not match rather than removing them —
 * removing them would collapse the floors and destroy the very shape that
 * makes this readable.
 */
export function LaunchBoardDesktopMirror({
  units,
  selectedUnitId,
  onSelectUnit,
}: LaunchBoardDesktopMirrorProps) {
  const { t } = useTranslation('features/launches');
  const [statusFilter, setStatusFilter] = useState<AllocationStatus | null>(null);
  const towers = useMemo(() => towersOf(units), [units]);

  const counts = useMemo(() => {
    const acc: Record<string, number> = {};
    units.forEach((u) => { acc[u.allocation_status] = (acc[u.allocation_status] ?? 0) + 1; });
    return acc;
  }, [units]);

  return (
    <div data-testid="launch-board-mirror" className="flex flex-col gap-[var(--space-4)]">
      <div className="flex flex-wrap gap-[var(--space-2)]">
        {STATUS_ORDER.map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setStatusFilter(statusFilter === status ? null : status)}
            className={cn(
              'flex items-center gap-[var(--space-2)] rounded-(--radius-full) border px-[var(--space-3)] py-[var(--space-1)]',
              'text-[length:var(--text-meta)]',
              statusFilter === status
                ? 'border-[var(--border-strong)] bg-[var(--surface-tertiary)]'
                : 'border-[var(--border-subtle)]',
            )}
          >
            <span className={cn('inline-block h-2 w-2 rounded-full', ALLOCATION_STATUS_DOT[status])} />
            <span className="text-[var(--text-secondary)]">{t(`status.${status}`)}</span>
            <span className="tabular-nums text-[var(--text-primary)]">{counts[status] ?? 0}</span>
          </button>
        ))}
      </div>

      {/* Towers sit side by side whenever two fit. A tower is about 370px
          wide with three units per floor, so a 1280px board was stacking
          them and leaving most of the width empty. auto-fit rather than a
          fixed column count: launches have one tower or four, and the
          number of units per floor varies. */}
      <div
        className="grid gap-[var(--space-4)]"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))' }}
      >
      {towers.map(([tower, towerUnits]) => (
        <section
          key={tower}
          className="flex flex-col gap-[var(--space-3)] rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-card)] p-[var(--space-4)]"
        >
          <Text
            as="span"
            className="text-[length:var(--text-app-heading)] [font-weight:var(--font-semibold)] text-[var(--text-primary)]"
          >
            {tower || t('board.singleTower')}
          </Text>

          {floorsOf(towerUnits).map(([floor, floorUnits]) => (
            <div key={floor} className="flex items-center gap-[var(--space-3)]">
              <Text
                as="span"
                className="w-[var(--space-10)] shrink-0 text-right text-[length:var(--text-meta)] text-[var(--text-tertiary)] tabular-nums"
              >
                {floor}
              </Text>
              <div className="flex flex-wrap gap-[var(--space-2)]">
                {floorUnits.map((unit) => (
                  <UnitCell
                    key={unit.unit_id}
                    unit={unit}
                    selected={unit.unit_id === selectedUnitId}
                    dimmed={statusFilter !== null && unit.allocation_status !== statusFilter}
                    onSelect={onSelectUnit}
                  />
                ))}
              </div>
            </div>
          ))}
        </section>
      ))}
      </div>
    </div>
  );
}
